/**
 * Full-screen single-day family order entry — `/orders/family/day?date`.
 *
 * TABS are the family's CHILDREN — every child, regardless of campus; a
 * child's tab shows THEIR campus's menu and deadline (`locationId` in the URL
 * is accepted for legacy links but the view always aggregates). Within a
 * child's tab the items are grouped by mealtime with a "breaker" row. Each row shows the **ordered quantity in green**
 * (what's already placed) beside a **+/- stepper**.
 *
 * Three modes, by whether the day already has a placed, still-editable order:
 *  - ADD (no order yet): steppers write to the cart; the footer checks out and
 *    places a new FAMILY_DIRECT order.
 *  - RECEIPT (order placed): a read-only list of only the ordered items with
 *    static quantities; an "Edit order" footer button switches into EDIT.
 *  - EDIT (entered from the receipt, window open): steppers seed from the placed
 *    quantities and edits are staged; "Save changes" commits via `replaceOrder`.
 *    Any change to a placed order shows a confirmation listing each before→after
 *    first.
 * When the week's cutoff has passed the day is read-only (receipt with no
 * "Edit order" button).
 *
 * The header shows just the day (date pill + weekday) and the close X; a
 * status row below it reads placed-ness plus the remaining window ("Order
 * placed · Edits due in 2 days"). The ADD footer's primary CTA walks the
 * week instead of chevrons: "Next day" targets the first later weekday that
 * still needs items, "Back to {Weekday}" wraps to the earliest unhandled
 * one, and "Review & Checkout" appears once no other day needs anything
 * (`@/lib/family-orders/week-cta`). The footer's cart total doubles as a
 * discreet "See cart" shortcut straight to checkout.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ordo/ui/alert-dialog'
import { Badge } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { EmptyState } from '@ordo/ui/empty-state'
import { MenuServiceBadge } from '@ordo/ui/menu-service-badge'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ordo/ui/tabs'
import { keepPreviousData, useQueries, useQueryClient } from '@tanstack/react-query'
import { CalendarOff, ChevronRight, ShoppingCart, UtensilsCrossed, X } from 'lucide-react'
import { Fragment, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import {
  type FamilyOrderPricePreviewLineWire,
  familyOrderPricePreviewQueryKey,
  type FamilyOrderWire,
  fetchFamilyOrderPricePreview,
  useCancelPaymentRetryMutation,
  useFamilyOrdersQuery,
  useReplaceFamilyOrderMutation,
  useRetryPaymentMutation,
} from '@/api/family-orders'
import {
  fetchLocationActiveMenus,
  type HolidayTreatWire,
  locationActiveMenusQueryKey,
  MENUS_STALE_TIME_MS,
  useLocationActiveMenusQuery,
} from '@/api/orders'
import {
  listOrderingStudents,
  type OrderingStudent,
  submitFamilyOrderGroup,
  useMyOrderingFamilyQuery,
} from '@/api/orders-family'
import { type Mealtime, serviceDateIso } from '@/api/school-orders'
import {
  fetchSpecialDietEligibleItems,
  specialDietEligibleItemsKey,
  type SpecialDietEligibleItemWire,
} from '@/api/special-diet'
import { AssetImage } from '@/components/asset-image'
import { DietaryTagChips } from '@/components/orders/dietary-tag-chips'
import { CartTotalPrice } from '@/components/orders/family/cart-total-price'
import { FamilyDayEmptyState } from '@/components/orders/family/day-empty-state'
import { HolidayTreatGiftRow } from '@/components/orders/family/holiday-treat-gift-row'
import { useCartPricePreview } from '@/components/orders/family/use-cart-price-preview'
import { useAutoOrderCoverage } from '@/components/orders/family/week/auto-order-coverage'
import {
  acceptsParentOrders,
  childLocationRow,
  type FamilyOrderLocation,
  groupStudentsByLocation,
  locationForChild,
} from '@/components/orders/family/week/columns'
import { buildDayBadges, type DayBadge, isDayClosed } from '@/components/orders/family/week/day-badges'
import { QtyStepper } from '@/components/orders/family/week/qty-stepper'
import { type ActiveMenuItem, menuItemImageUri, menuItemName } from '@/components/orders/school/day-order-table'
import { MenuItemDetailModal } from '@/components/orders/school/menu-item-detail-modal'
import { UserAvatar } from '@/components/user-identity-card'
import {
  buildFamilyAlreadyOrdered,
  buildFamilyInactiveLines,
  type InactiveOrderLine,
} from '@/lib/family-orders/already-ordered'
import { autoOrderSkippedMealtimes } from '@/lib/family-orders/auto-order-skips'
import {
  allItemsByMealtime,
  type ChildBandContext,
  type MenuGroupAnnotation,
  orderableItemsByMealtime,
  withAdultPrices,
} from '@/lib/family-orders/band-menus'
import { cartLineKey, type FamilyCartLine, groupKey, subtotalCents, totalQty } from '@/lib/family-orders/cart'
import { type DayBadgeTone, formatDayStatusSummary } from '@/lib/family-orders/day-status'
import { resolveStudentNoMenuReason } from '@/lib/family-orders/no-menu-reason'
import {
  diffOrderChanges,
  editDeltaCents,
  type OrderChange,
  type OrderDeltaCell,
} from '@/lib/family-orders/order-changes'
import { batchesByMealtimeForDay, type DesiredOrderLine, planEditCommit } from '@/lib/family-orders/order-commit'
import {
  describeAutomaticRetry,
  isPaymentError,
  parsePaymentShortfall,
  paymentShortfallLine,
} from '@/lib/family-orders/payment-pending'
import {
  buildPreviewLookup,
  previewBadge,
  previewLineKey,
  type PreviewStudentAllowance,
  resolveSittingAllowance,
  scopedPreviewEntries,
  sittingStudentKey,
} from '@/lib/family-orders/price-preview'
import { conflictsWithSpecialDietSelection } from '@/lib/family-orders/special-diet-selection'
import { dayFillable, dayNeedsItems, resolveWeekCta } from '@/lib/family-orders/week-cta'
import { MEALTIMES as SPECIAL_DIET_MEALTIMES } from '@/lib/mealtime'
import { withReturnTo } from '@/lib/return-to'
import { dateFromKey, mondayOf, todayKey, weekOf } from '@/lib/school-orders/calendar'
import { MEALTIME_LABELS, sortMealtimes } from '@/lib/school-orders/mealtime'
import { formatCents } from '@/lib/school-orders/money'
import { useAllCartLines, useFamilyCart, useFamilyCartSync } from '@/stores/family-cart'

const TABS_LIST_CLASS = 'h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0'
const TABS_TRIGGER_CLASS =
  'relative -mb-px flex items-center gap-2 rounded-none border-b-2 border-transparent bg-transparent px-4 pb-2.5 pt-2.5 text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'

function editKey(mealtime: Mealtime, userId: string, itemId: string): string {
  return `${mealtime}::${userId}::${itemId}`
}

/**
 * Day-view shell. Below 1200px it's a full-screen sheet over the app chrome.
 * At ≥1200px (desktop only) it becomes a centered modal capped at 1200px wide
 * over a dimmed backdrop; everything narrower keeps the full-screen behavior.
 */
function Sheet({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex bg-background min-[1200px]:items-center min-[1200px]:justify-center min-[1200px]:bg-black/50 min-[1200px]:p-6">
      <div className="flex h-full min-h-0 w-full flex-col bg-background min-[1200px]:h-[90vh] min-[1200px]:max-w-[1200px] min-[1200px]:overflow-hidden min-[1200px]:rounded-2xl min-[1200px]:shadow-2xl">
        {children}
      </div>
    </div>
  )
}

/** "Monday" — long weekday of a date key; the header title + wrap-back CTA label. */
function weekdayLong(dateKey: string): string {
  return dateFromKey(dateKey).toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' })
}

function DayHeader({ dateKey, onClose }: { dateKey: string; onClose: () => void }): ReactElement {
  const d = dateFromKey(dateKey)
  const monthAbbr = d.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }).toUpperCase()
  const dayNum = d.getUTCDate()
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-md bg-muted leading-none">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{monthAbbr}</span>
          <span className="text-sm font-semibold text-foreground">{dayNum}</span>
        </span>
        <h1 className="truncate text-lg font-medium tracking-tight text-foreground">{weekdayLong(dateKey)}</h1>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

const STATUS_DOT: Record<DayBadgeTone, string> = {
  info: 'bg-blue-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  neutral: 'bg-zinc-400 dark:bg-zinc-500',
}

/**
 * Status strip between the header and the child tabs — replaces the old
 * header pill: placed-ness plus the remaining window, e.g. "Order placed ·
 * Edits due in 2 days" / "No order placed · Due tomorrow". Days with a
 * placed order carry a light success tint so the state reads at a glance.
 * Hidden when the day has nothing to say (no deadline policy and nothing
 * ordered).
 */
function DayStatusRow({ badge, timeZone }: { badge: DayBadge | undefined; timeZone: string }): ReactElement | null {
  if (!badge) {
    return null
  }
  const summary = formatDayStatusSummary(badge.status, badge.deadline, timeZone, new Date())
  if (!summary) {
    return null
  }
  // The statuses whose lead reads "Order placed" (editable or locked).
  const placed = badge.status === 'EDIT_BY' || badge.status === 'COMPLETE'
  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-2 border-b px-4 py-2 text-sm',
        placed && 'bg-emerald-50 dark:bg-emerald-950/40',
      )}>
      <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[badge.tone])} />
      <span className="font-medium text-foreground">{summary.lead}</span>
      {summary.detail ? (
        <span className="truncate text-muted-foreground">
          <span aria-hidden>· </span>
          {summary.detail}
        </span>
      ) : null}
    </div>
  )
}

export function FamilyDayPage(): ReactElement {
  const navigate = useNavigate()
  // Hydrate the server-synced cart so quantities entered on another
  // device pre-fill this day's steppers.
  useFamilyCartSync()
  const [params] = useSearchParams()
  const requestedLocationId = params.get('locationId')
  const date = params.get('date') ?? ''

  const familyQuery = useMyOrderingFamilyQuery()
  const students = useMemo(() => listOrderingStudents(familyQuery.data), [familyQuery.data])
  const locations = useMemo(() => groupStudentsByLocation(students), [students])
  // Legacy deep links carry a locationId — use it only to pick which child's
  // tab opens first; the view itself always shows every child.
  const initialChild = useMemo(
    () => (requestedLocationId ? students.find((c) => c.locations.some((l) => l.id === requestedLocationId)) : null),
    [students, requestedLocationId],
  )

  const close = (): void => {
    void navigate('/orders/family')
  }

  if (familyQuery.isPending && !familyQuery.data) {
    return (
      <Sheet>
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-10 w-40" />
          <Skeleton className="mt-2 h-64 w-full" />
        </div>
      </Sheet>
    )
  }
  if (locations.length === 0 || !date) {
    return (
      <Sheet>
        <div className="flex h-full flex-col">
          <DayHeader dateKey={date || '1970-01-01'} onClose={close} />
          <p className="p-4 text-sm text-muted-foreground">Day not found.</p>
        </div>
      </Sheet>
    )
  }
  return (
    <DayBody
      locations={locations}
      students={students}
      date={date}
      initialChildId={initialChild?.accountMemberId ?? null}
      onClose={close}
    />
  )
}

/**
 * A day-page row item. Normal menu items are plain `ActiveMenuItem`s; a
 * doctor's-note special-diet item the active child is approved for carries its
 * accommodation id + per-student daily cap so the row can tag it and the cart
 * can thread enforcement.
 */
/** A menu item annotated with its owning menu's section (see band-menus). */
type DayMenuItem = ActiveMenuItem & MenuGroupAnnotation

type DayRowItem = DayMenuItem & {
  specialDietAccommodationId?: string
  specialDietMaxPerDay?: number | null
}

/** Build a day-page row from a special-diet eligible-item wire (menu-priced). */
function specialDietWireToRow(wire: SpecialDietEligibleItemWire): DayRowItem | null {
  if (!wire.menuItemId) {
    // Menu-level assignment with no concrete item can't form an orderable row.
    return null
  }
  return {
    id: wire.menuItemId,
    sectionId: null,
    itemKind: wire.itemKind ?? 'COMPONENT',
    name: wire.name,
    // Sort special-diet-only additions after the normal menu items.
    sortOrder: Number.MAX_SAFE_INTEGER,
    visible: true,
    isArchived: false,
    // Priced by the normal menu-level pricing preview (keyed by menuItemId),
    // exactly like every other item — never treated as unpriced.
    customerPriceCents: null,
    currency: null,
    isPriced: true,
    allergens: null,
    allowedLocationGradeRangeIds: [],
    allowedGroupScopes: null,
    bundle: null,
    component: {
      id: wire.menuItemId,
      name: wire.name,
      displayName: null,
      description: null,
      imageUri: wire.imageUri,
      kind: wire.componentKind ?? 'MAIN_ENTREE',
    },
    menuGroupTitle: 'Special Diets',
    specialDietAccommodationId: wire.accommodationId,
    specialDietMaxPerDay: wire.dailyCapacity,
  }
}

/** A desired line as the replace / submit wires want it (no billing account). */
function toOrderLinePayload(line: DesiredOrderLine): {
  menuItemId: string
  studentUserId: string
  quantity: number
  specialDietAccommodationId?: string
} {
  return {
    menuItemId: line.menuItemId,
    studentUserId: line.studentUserId,
    quantity: line.quantity,
    ...(line.specialDietAccommodationId ? { specialDietAccommodationId: line.specialDietAccommodationId } : {}),
  }
}

/** Dedupe key for a (student, item, accommodation) order line. */
function orderLineKey(userId: string, menuItemId: string, accommodationId: string | null | undefined): string {
  return `${userId}::${menuItemId}::${accommodationId ?? ''}`
}

/**
 * Reconstruct a day-page row from a PLACED order line, so an EDIT can
 * preserve a line the child's live row list can't represent — a special-diet
 * line of a non-active child, or a line whose menu is no longer offered to
 * the child (band re-tag, menu drift). replaceOrder replaces the whole
 * batch, so an omitted line would be silently dropped (and credited).
 */
function placedLineToRow(line: {
  menuItemId: string
  specialDietAccommodationId?: string | null
  title: string
  imageUri: string | null
  pricing?: { grossCents: number; currency: string }
}): DayRowItem {
  return {
    id: line.menuItemId,
    sectionId: null,
    itemKind: 'COMPONENT',
    name: line.title,
    sortOrder: Number.MAX_SAFE_INTEGER,
    visible: true,
    isArchived: false,
    customerPriceCents: line.pricing?.grossCents ?? null,
    currency: line.pricing?.currency ?? null,
    isPriced: true,
    allergens: null,
    allowedLocationGradeRangeIds: [],
    allowedGroupScopes: null,
    bundle: null,
    component: {
      id: line.menuItemId,
      name: line.title,
      displayName: null,
      description: null,
      imageUri: line.imageUri,
      kind: 'MAIN_ENTREE',
    },
    specialDietAccommodationId: line.specialDietAccommodationId ?? undefined,
    specialDietMaxPerDay: null,
  }
}

/** Receipts retain frozen lines even when their menu is no longer available. */
export function preservePlacedReceiptRows(
  rows: readonly DayRowItem[],
  placed: ReadonlyArray<Parameters<typeof placedLineToRow>[0]>,
): DayRowItem[] {
  const result = [...rows]
  const represented = new Set(rows.map((row) => row.id))
  for (const line of placed) {
    if (!represented.has(line.menuItemId)) {
      represented.add(line.menuItemId)
      result.push(placedLineToRow(line))
    }
  }
  return result
}

/** Carry a missing child/item identity once across legacy duplicate batches. */
export function unrepresentedPlacedLines(
  lines: ReadonlyArray<FamilyOrderWire['items'][number]>,
  represented: ReadonlySet<string>,
): FamilyOrderWire['items'] {
  const seen = new Set(represented)
  return lines.filter((line) => {
    if (!line.studentUserId) {
      return false
    }
    const key = orderLineKey(line.studentUserId, line.menuItemId, line.specialDietAccommodationId)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

/** Keep a placed meal's identity when current ordering eligibility disappears. */
export function preservePlacedSpecialDietRows(
  rows: readonly DayRowItem[],
  placed: ReadonlyArray<Parameters<typeof placedLineToRow>[0]>,
): DayRowItem[] {
  const result = [...rows]
  const indexById = new Map(result.map((row, index) => [row.id, index]))
  for (const line of placed) {
    if (!line.specialDietAccommodationId) {
      continue
    }
    const index = indexById.get(line.menuItemId)
    const row = {
      ...(index === undefined ? placedLineToRow(line) : result[index]!),
      menuGroupTitle: 'Special Diets',
      specialDietAccommodationId: line.specialDietAccommodationId,
      specialDietMaxPerDay: 1,
    }
    if (index === undefined) {
      indexById.set(line.menuItemId, result.length)
      result.push(row)
    } else {
      result[index] = row
    }
  }
  return result
}

function DayBody({
  locations,
  students,
  date,
  initialChildId,
  onClose,
}: {
  locations: FamilyOrderLocation[]
  students: OrderingStudent[]
  date: string
  initialChildId: string | null
  onClose: () => void
}): ReactElement {
  // Every child across every campus — the active tab decides which campus's
  // menu, deadline, and placed orders drive the panel below.
  const children = students
  const [activeChildId, setActiveChildId] = useState<string | null>(initialChildId)
  const activeChild = children.find((c) => c.accountMemberId === activeChildId) ?? children[0] ?? null
  const location = (activeChild ? locationForChild(locations, activeChild) : null) ?? locations[0]!
  const isoDate = serviceDateIso(date)

  const { data, isPending } = useLocationActiveMenusQuery({
    locationId: location.id,
    activeOn: isoDate,
    withAllergens: true,
  })
  const menusForDay = useMemo(() => data?.menusForLocation ?? [], [data])

  // Receipts render from the UNGATED flatten: a placed order must stay
  // visible even when its menu is no longer offered to the child (menu
  // re-tagged, band changed). Receipt mode only renders ordered items, so
  // nothing off-band leaks through this map.
  const receiptItemsByMealtime = useMemo(() => allItemsByMealtime(menusForDay), [menusForDay])

  // ORDERABLE rows are per child: menus filter by the child's grade band
  // (shared `menuAllowsGradeRangeCode` gate; staff self-diners skip it)
  // before flattening by mealtime. The same item at two portion sizes is two
  // menu instances tagged for different bands — each child sees only their
  // own instance, so no dedupe is ever needed. A band-less STUDENT sees zero
  // menus (fail-closed); the server enforces the same rule at submit.
  const orderableItemsByChild = useMemo(() => {
    const out = new Map<string, Map<Mealtime, DayMenuItem[]>>()
    for (const child of children) {
      const row = childLocationRow(child, location.id)
      const items = orderableItemsByMealtime(menusForDay, {
        // Entitlement (restricted-diet menus) is matched on the child's
        // central user id — the server sends one payload per household.
        userId: child.userId,
        // No roster row at this campus reads as a band-less student —
        // fail-closed (nothing orderable here for them).
        bandGated: row ? row.isStudent : true,
        gradeRangeCode: row?.gradeRangeCode ?? null,
      })
      // An adult diner (staff self-diner) is charged the adult price.
      out.set(child.accountMemberId, row?.isStudent === false ? withAdultPrices(items) : items)
    }
    return out
  }, [menusForDay, children, location.id])
  const orderableFor = (child: OrderingStudent): Map<Mealtime, DayMenuItem[]> =>
    orderableItemsByChild.get(child.accountMemberId) ?? new Map<Mealtime, DayMenuItem[]>()

  // Union of the campus children's orderable mealtimes — the edit/commit
  // iteration base; each child's own list still decides their rows.
  const regularMealtimes = useMemo(() => {
    const keys = new Set<Mealtime>()
    for (const child of location.children) {
      for (const key of orderableItemsByChild.get(child.accountMemberId)?.keys() ?? []) {
        keys.add(key)
      }
    }
    return sortMealtimes(keys)
  }, [orderableItemsByChild, location.children])

  const ordersQuery = useFamilyOrdersQuery({ weekStart: mondayOf(date) })
  const weekOrders = useMemo(() => ordersQuery.data?.orders ?? [], [ordersQuery.data])
  const alreadyOrdered = useMemo(() => buildFamilyAlreadyOrdered(weekOrders), [weekOrders])
  // PROD-1037: a child's enabled auto-order that stood down for this date
  // (slot already hand-ordered) gets a caption under its mealtime header —
  // a silent skip reads as "auto-order broke".
  const autoOrderCoverage = useAutoOrderCoverage(children, location.id)
  const autoOrderSkippedFor = (child: OrderingStudent): ReadonlySet<Mealtime> =>
    new Set(
      autoOrderSkippedMealtimes({
        dateKey: date,
        studentUserId: child.userId,
        subscription: autoOrderCoverage?.subByUserId.get(child.userId),
        alreadyOrdered,
      }),
    )

  const { badges } = useMemo(() => buildDayBadges(location, weekOf(date), weekOrders), [location, date, weekOrders])
  const badge = badges[date]
  // View-only = the campus takes no parent orders (school ordering only,
  // PROD-1554): the day shows its menu with item details, no steppers, no
  // prices, no cart footer. Closed = weekly deadline passed OR the day is in
  // the past (past days are never orderable, even with no deadline policy).
  // Either way the page is read-only.
  const viewOnly = !acceptsParentOrders(location)
  const readOnly = viewOnly || isDayClosed(location, date)

  // Per-mealtime placed batches for this day (drives edit mode). A LIST per
  // mealtime, never one batch: the checkout writes one batch per household,
  // so a household that ordered its children on separate visits holds one
  // batch per child on the same sitting. Collapsing them made the edit save
  // post one child's lines into a sibling's batch (PROD-1295). Scoped to the
  // ACTIVE child's campus — with kids at two campuses the same (date,
  // mealtime) also holds one batch per campus.
  const batchesByMealtime = useMemo(
    () => batchesByMealtimeForDay(weekOrders, { dateKey: date, serviceLocationId: location.id }),
    [weekOrders, date, location.id],
  )
  const placedBatches = useMemo(() => [...batchesByMealtime.values()].flat(), [batchesByMealtime])
  const hasPlacedOrder = batchesByMealtime.size > 0
  // Cancelled/refunded lines for this day+campus — rendered on the receipt as
  // muted rows with a badge (they no longer count as ordered anywhere else).
  const inactiveLineFor = useMemo(
    () => buildFamilyInactiveLines(weekOrders, { dateKey: date, serviceLocationId: location.id }),
    [weekOrders, date, location.id],
  )
  const hasEditableOrder = useMemo(() => placedBatches.some((o) => o.editable), [placedBatches])
  // Payment-pending DRAFTs: the checkout debit failed. Rendered as a receipt
  // with a banner (add funds / cancel) — never as a placed order. A NULL
  // `paymentRetryAt` is a pre-retry-feature batch: same unpaid state, same
  // treatment, just no scheduled retry to mention.
  //
  // Sourced from weekOrders directly — `batchesByMealtime` keeps only the
  // PLACED batches. Pre-fix checkout retries stacked several DRAFT zombies on
  // the same (day, mealtime); cancel must void ALL of them or the day stays
  // "Payment pending" after the button reports success.
  const pendingPaymentBatches = useMemo(
    () =>
      weekOrders.filter(
        (o) => o.status === 'DRAFT' && o.serviceDate.slice(0, 10) === date && o.serviceLocationId === location.id,
      ),
    [weekOrders, date, location.id],
  )
  const hasPendingPayment = pendingPaymentBatches.length > 0
  const hasScheduledRetry = pendingPaymentBatches.some((o) => o.paymentRetryAt)
  // What happens if the parent does nothing — from the schedule the server
  // stamped (retry clamped to the cutoff, PROD-1299), never "within 24 hours".
  const automaticRetryClause = useMemo(
    () =>
      describeAutomaticRetry(
        pendingPaymentBatches.map((o) => ({ paymentRetryAt: o.paymentRetryAt, orderingCutoffAt: o.deadlineAt })),
        new Date(),
        location.timeZone,
      ),
    [pendingPaymentBatches, location.timeZone],
  )

  // Why is there nothing to order on this day? Drives the differentiated
  // empty states (school break / not posted yet / holiday / …). A term
  // calendar NO_SERVICE day suppresses the item list even when a menu spans
  // the date — unless the family already placed an order, which they must
  // still be able to see.
  //
  // Resolved for the ACTIVE child — the only mounted panel (TabsContent
  // unmounts inactive tabs): a band-gated student whose grade has no band
  // mapping gets the self-diagnosing `grade-band-unmapped` reason instead of
  // a generic "no menu". If these tabs ever forceMount, thread the child in
  // per-panel instead.
  const noMenuReason = useMemo(
    () =>
      resolveStudentNoMenuReason({
        date,
        today: todayKey(),
        menus: data?.menusForLocation ?? [],
        dayContext: data?.dayContext,
        student: activeChild
          ? {
              displayName: activeChild.displayName,
              isSelf: activeChild.isSelf,
              location: childLocationRow(activeChild, location.id),
            }
          : null,
      }),
    [data, date, activeChild, location.id],
  )
  // Suppress the ordering UI on any day the school isn't serving — a term
  // calendar NO_SERVICE day, a blocked date, or a weekday switched off on the
  // menu's service days — unless the family already placed an order, which they
  // must still be able to see. Keyed off the resolved REASON rather than
  // `dayContext` alone: the mask and blockedDates arrive per menu, not on the
  // day context, and gating on the day context alone is how a non-service
  // Wednesday kept its "Order by …" countdown (Ops, 2026-08-04).
  const noServiceOverride = noMenuReason.kind === 'no-service-day' && !hasPlacedOrder

  // A placed day opens as a read-only RECEIPT; "Edit order" toggles into the
  // editable stepper view. A fresh day goes straight to the ADD steppers.
  const [editing, setEditing] = useState(false)
  const lastLocationId = useRef(location.id)
  useEffect(() => {
    if (lastLocationId.current !== location.id) {
      lastLocationId.current = location.id
      setEditing(false)
      setEditDraft({})
    }
  }, [location.id])
  // Walking to another day resets the staged edit — edit-draft keys aren't
  // date-scoped.
  const lastDate = useRef(date)
  useEffect(() => {
    if (lastDate.current !== date) {
      lastDate.current = date
      setEditing(false)
      setEditDraft({})
    }
  }, [date])
  const canEdit = !readOnly && hasEditableOrder
  const mode: 'add' | 'receipt' | 'edit' = hasPlacedOrder ? (editing && canEdit ? 'edit' : 'receipt') : 'add'

  // Cart (ADD mode; all campuses — line keys are globally unique) + edit
  // draft (EDIT mode). Edit state resets when the active tab switches campus.
  const cartLines = useAllCartLines()
  const setLine = useFamilyCart((s) => s.setLine)
  const cartQtyByKey = useMemo(() => new Map(cartLines.map((l) => [l.key, l.quantity])), [cartLines])
  const [editDraft, setEditDraft] = useState<Record<string, number>>({})
  const dirty = Object.keys(editDraft).length > 0

  // Fill-the-week CTA (`@/lib/family-orders/week-cta`): the ADD footer's
  // primary button walks the parent through the week. Cart/order signals are
  // family-wide; closed + fillable follow the ACTIVE child's campus, like
  // everything else on this sheet.
  const weekKeys = useMemo(() => weekOf(date), [date])
  const cartDates = useMemo(() => new Set(cartLines.map((l) => l.date)), [cartLines])
  const orderDates = useMemo(
    () =>
      new Set(
        weekOrders
          // Cancelled/refunded orders (derived `state`) don't fill a day —
          // the fill-the-week CTA should still walk the parent there.
          .filter((o) => o.status !== 'VOIDED' && (o.state ?? 'active') === 'active')
          .map((o) => o.serviceDate.slice(0, 10)),
      ),
    [weekOrders],
  )
  const bandContexts = useMemo(
    () =>
      location.children.map((child): ChildBandContext => {
        const row = childLocationRow(child, location.id)
        // No roster row at this campus reads as a band-less student — fail-closed.
        return {
          userId: child.userId,
          bandGated: row ? row.isStudent : true,
          gradeRangeCode: row?.gradeRangeCode ?? null,
        }
      }),
    [location],
  )
  // Sibling-day menus, fetched with the page query's own key/args so walking
  // the week lands on a warm cache. `enabled` mirrors `dayNeedsItems`'s cheap
  // short-circuits — a day already decided by cart/order/closed signals never
  // needs its fillability resolved. No keepPreviousData here: a date change
  // reshuffles the queries array and would mismap old data onto other days.
  const otherWeekDays = useMemo(() => weekKeys.filter((k) => k !== date), [weekKeys, date])
  const siblingMenus = useQueries({
    queries: otherWeekDays.map((dayKey) => {
      const args = { locationId: location.id, activeOn: serviceDateIso(dayKey), withAllergens: true }
      return {
        queryKey: locationActiveMenusQueryKey(args),
        queryFn: ({ signal }: { signal: AbortSignal }) => fetchLocationActiveMenus(args, signal),
        staleTime: MENUS_STALE_TIME_MS,
        enabled:
          mode === 'add' &&
          !readOnly &&
          !isDayClosed(location, dayKey) &&
          !cartDates.has(dayKey) &&
          !orderDates.has(dayKey),
      }
    }),
    combine: (results) => new Map(otherWeekDays.map((k, i) => [k, results[i]?.data ?? null])),
  })
  const weekCta = resolveWeekCta({
    weekKeys,
    currentDate: date,
    needsItems: (dayKey) =>
      dayNeedsItems({
        hasCartLine: cartDates.has(dayKey),
        hasPlacedOrder: orderDates.has(dayKey),
        closed: isDayClosed(location, dayKey),
        fillable: dayFillable(siblingMenus.get(dayKey), bandContexts),
      }),
  })

  // A restricted meal can be the child's only option at a mealtime.
  // Do not require an ordinary menu row before asking for approved choices.
  const specialDietMealtimes = activeChild ? SPECIAL_DIET_MEALTIMES : []

  // Doctor's-note special-diet items the ACTIVE child is approved to order —
  // fetched per orderable mealtime (like the price preview), listed inline
  // in a Special Diets section. Approval + per-student daily cap
  // are enforced server-side; ordering threads the accommodation id.
  const specialDietByMealtime = useQueries({
    queries: specialDietMealtimes.map((mealtime) => {
      const args = activeChild
        ? { accountId: activeChild.accountId, studentUserId: activeChild.userId, mealtime, serviceDate: isoDate }
        : null
      return {
        queryKey: args
          ? specialDietEligibleItemsKey(args)
          : (['special-diet', 'eligible-items', '_disabled_', mealtime] as const),
        queryFn: ({ signal }: { signal: AbortSignal }) => fetchSpecialDietEligibleItems(args!, signal),
        enabled: args !== null,
        staleTime: 30_000,
      }
    }),
    combine: (results) =>
      new Map<Mealtime, SpecialDietEligibleItemWire[]>(
        results.flatMap((result, index) =>
          result.data?.items.length ? ([[specialDietMealtimes[index] as Mealtime, result.data.items]] as const) : [],
        ),
      ),
  })

  // The active child's rows = base menu items + their approved special-diet
  // items. An eligible item that IS already a normal menu row is tagged in
  // place (no duplicate); one that isn't is appended.
  const mergeSpecialDietRows = useCallback(
    (base: ReadonlyMap<Mealtime, DayMenuItem[]>): Map<Mealtime, DayRowItem[]> => {
      const merged = new Map<Mealtime, DayRowItem[]>()
      const allMealtimes = new Set<Mealtime>([...base.keys(), ...specialDietByMealtime.keys()])
      for (const mealtime of allMealtimes) {
        const rows: DayRowItem[] = [...(base.get(mealtime) ?? [])]
        const indexById = new Map(rows.map((row, i) => [row.id, i]))
        for (const wire of specialDietByMealtime.get(mealtime) ?? []) {
          if (!wire.menuItemId) {
            continue
          }
          const existing = indexById.get(wire.menuItemId)
          if (existing != null) {
            rows[existing] = {
              ...rows[existing]!,
              menuGroupTitle: 'Special Diets',
              specialDietAccommodationId: wire.accommodationId,
              specialDietMaxPerDay: wire.dailyCapacity,
            }
          } else {
            const row = specialDietWireToRow(wire)
            if (row) {
              rows.push(row)
            }
          }
        }
        merged.set(mealtime, [
          ...rows.filter((row) => !row.specialDietAccommodationId),
          ...rows.filter((row) => row.specialDietAccommodationId),
        ])
      }
      return merged
    },
    [specialDietByMealtime],
  )
  const activeChildItemsByMealtime = useMemo(
    () => mergeSpecialDietRows(activeChild ? orderableFor(activeChild) : new Map<Mealtime, DayMenuItem[]>()),
    // orderableFor is a thin lookup over orderableItemsByChild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergeSpecialDietRows, orderableItemsByChild, activeChild],
  )
  // Receipt rows for the ACTIVE child keep the special-diet merge so a placed
  // doctor's-note line still renders, over the UNGATED base so placed
  // off-band lines stay visible too.
  const activeChildReceiptItemsByMealtime = useMemo(
    () => mergeSpecialDietRows(receiptItemsByMealtime),
    [mergeSpecialDietRows, receiptItemsByMealtime],
  )
  const activeChildMealtimes = useMemo(
    () => sortMealtimes(activeChildItemsByMealtime.keys()),
    [activeChildItemsByMealtime],
  )
  const mealtimes = useMemo(
    () => sortMealtimes(new Set([...regularMealtimes, ...activeChildMealtimes])),
    [regularMealtimes, activeChildMealtimes],
  )
  // The list a child's rows read from. RECEIPT mode renders the ungated maps
  // (only ordered items show, and they must show even when band-blocked
  // today); ADD/EDIT offer each child their band-gated orderable list.
  const rowsByMealtime = (child: OrderingStudent): ReadonlyMap<Mealtime, DayRowItem[]> => {
    const isActiveChild = activeChild !== null && child.accountMemberId === activeChild.accountMemberId
    const base =
      mode === 'receipt'
        ? isActiveChild
          ? activeChildReceiptItemsByMealtime
          : receiptItemsByMealtime
        : isActiveChild
          ? activeChildItemsByMealtime
          : orderableFor(child)
    if (mode === 'add') {
      return base
    }
    const rows = new Map<Mealtime, DayRowItem[]>(base)
    for (const [mealtime, batches] of batchesByMealtime) {
      rows.set(
        mealtime,
        preservePlacedSpecialDietRows(
          mode === 'receipt'
            ? preservePlacedReceiptRows(
                rows.get(mealtime) ?? [],
                batches.flatMap((batch) => batch.items.filter((line) => line.studentUserId === child.userId)),
              )
            : (rows.get(mealtime) ?? []),
          batches.flatMap((batch) => batch.items.filter((line) => line.studentUserId === child.userId)),
        ),
      )
    }
    return rows
  }
  const mealtimesFor = (child: OrderingStudent): Mealtime[] => sortMealtimes(rowsByMealtime(child).keys())

  // Personalized prices for the ACTIVE child's rows — one server preview
  // per mealtime present on the day (browse mode: each item priced as if
  // it were the child's one meal for that sitting; the per-sitting
  // subsidy allowance is cross-batch server state, so no local math).
  const queryClient = useQueryClient()
  const browsePreview = useQueries({
    queries: activeChildMealtimes.map((mealtime) => {
      const items = (activeChildItemsByMealtime.get(mealtime) ?? []).filter((item) => item.isPriced !== false)
      const vars = {
        serviceLocationId: location.id,
        serviceDate: isoDate,
        mealtime,
        mode: 'browse' as const,
        lines: items.map((item) => ({
          menuItemId: item.id,
          studentUserId: activeChild?.userId ?? '',
          quantity: 1,
        })),
      }
      return {
        queryKey: familyOrderPricePreviewQueryKey(vars),
        // The mealtime rides INSIDE the query data (`scope`): results are
        // paired by it, never by array index — see `scopedPreviewEntries`.
        queryFn: async ({ signal }: { signal: AbortSignal }) => ({
          scope: mealtime as string,
          preview: await fetchFamilyOrderPricePreview(vars, signal),
        }),
        enabled: activeChild !== null && vars.lines.length > 0,
        staleTime: 30_000,
        // Key changes (child tab switch, post-save invalidation) keep the
        // previous preview rendered instead of flashing rows to unpriced.
        placeholderData: keepPreviousData,
      }
    }),
    combine: (results) => {
      const entries = scopedPreviewEntries(results)
      return {
        byKey: buildPreviewLookup(entries),
        // Draft-cart-aware allowance state per mealtime (browse mode) —
        // the fallback when the cart has no lines for the sitting.
        studentsByMealtime: new Map<Mealtime, readonly PreviewStudentAllowance[]>(
          entries.map((entry) => [entry.scope as Mealtime, entry.students]),
        ),
      }
    },
  })
  // Cart-aware allowance state (submit-mode preview over the cart's own
  // lines) — once the cart consumes the sitting's allowance, the other
  // rows must stop advertising "covered".
  const cartPreview = useCartPricePreview()
  const previewFor = (
    child: OrderingStudent,
    mealtime: Mealtime,
    item: ActiveMenuItem,
  ): FamilyOrderPricePreviewLineWire | null => {
    if (!activeChild || child.accountMemberId !== activeChild.accountMemberId) {
      return null
    }
    const line = browsePreview.byKey.get(previewLineKey(mealtime, child.userId, item.id)) ?? null
    if (!line || line.sponsoredCents <= 0) {
      return line
    }
    // Sponsored line: suppress the covered/reduced display when the
    // sitting's allowance is already spoken for (prior orders + cart).
    const remaining = resolveSittingAllowance(
      cartPreview.studentsBySitting.get(sittingStudentKey(groupKey(date, mealtime), child.userId)),
      browsePreview.studentsByMealtime.get(mealtime),
      child.userId,
    )
    return remaining !== null && remaining <= 0 ? null : line
  }

  const checkoutNavigate = useNavigate()
  // Current URL (day + date params) — the billing page's "Back to your
  // order" CTA returns the parent exactly here after a top-up.
  const currentLocation = useLocation()
  const returnHerePath = `${currentLocation.pathname}${currentLocation.search}`
  const goToCheckout = (): void => {
    void checkoutNavigate('/orders/family/checkout')
  }
  const replaceMutation = useReplaceFamilyOrderMutation()
  const cancelRetryMutation = useCancelPaymentRetryMutation()
  const cancelPaymentRetry = async (): Promise<void> => {
    try {
      for (const order of pendingPaymentBatches) {
        await cancelRetryMutation.mutateAsync({ orderBatchId: order.orderBatchId })
      }
      toast.success('Order cancelled', {
        description: 'Nothing was charged.',
      })
    } catch (error) {
      toast.error('Could not cancel the retry', {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }
  // "Complete order now" — immediate wallet-debit retry on every pending
  // batch (launch-gap 09: the parent just added funds via the returnTo
  // loop; nobody should wait for the 24h automatic retry). A 402 means
  // the balance still doesn't cover it — the batch stays pending.
  const retryPaymentMutation = useRetryPaymentMutation()
  const retryPaymentNow = async (): Promise<void> => {
    try {
      for (const order of pendingPaymentBatches) {
        await retryPaymentMutation.mutateAsync({ orderBatchId: order.orderBatchId })
      }
      toast.success('Order completed', {
        description: 'Payment went through — your meals are ordered.',
      })
    } catch (error) {
      if (isPaymentError(error)) {
        const shortfall = parsePaymentShortfall(error.body)
        const line = paymentShortfallLine(shortfall)
        // SPONSOR = the school's meal-plan wallet fell short — "add funds"
        // would tell the parent to fix an account they can't touch.
        toast.error(shortfall.payer === 'SPONSOR' ? 'School meal-plan balance is out' : 'Still not enough funds', {
          description: line ?? 'Your balance doesn’t cover this order yet — add funds and try again.',
        })
        return
      }
      toast.error('Could not complete the order', {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }
  const [pendingChanges, setPendingChanges] = useState<OrderChange[] | null>(null)
  const [saving, setSaving] = useState(false)
  // Leaving a DIRTY edit needs a confirm first — 'exit' (ESC) drops back to
  // the receipt, 'close' (header X) also closes the day view.
  const [discardIntent, setDiscardIntent] = useState<'exit' | 'close' | null>(null)

  // ESC steps back one layer: EDIT drops back to the receipt (via the
  // discard confirm when the draft is dirty), otherwise the day view
  // closes. Nested Radix overlays (confirm dialogs, item-detail modal)
  // own their own ESC — while one is open, or once something upstream
  // handled the key, do nothing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return
      }
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
        return
      }
      event.preventDefault()
      if (mode === 'edit') {
        if (saving) {
          return
        }
        if (dirty) {
          setDiscardIntent('exit')
        } else {
          setEditing(false)
        }
        return
      }
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mode, saving, dirty, onClose])

  // Header X: a dirty edit confirms before the day view closes.
  const requestClose = (): void => {
    if (mode === 'edit' && dirty && !saving) {
      setDiscardIntent('close')
      return
    }
    onClose()
  }

  // Footer CTA: walk to another weekday in place. Replace-navigation, so
  // stepping through days doesn't stack history — Back always leaves the
  // day view in one step.
  const goToDay = (dayKey: string): void => {
    void checkoutNavigate(`/orders/family/day?date=${encodeURIComponent(dayKey)}`, { replace: true })
  }

  const confirmDiscard = (): void => {
    const intent = discardIntent
    setDiscardIntent(null)
    setEditDraft({})
    setEditing(false)
    if (intent === 'close') {
      onClose()
    }
  }

  const dayLabel = useMemo(() => {
    const d = dateFromKey(date)
    const weekday = d.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' })
    const month = d.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
    return `${weekday} ${month} ${d.getUTCDate()}`
  }, [date])

  const orderedQty = (child: OrderingStudent, mealtime: Mealtime, item: ActiveMenuItem): number =>
    alreadyOrdered.cellQty(date, mealtime, child.userId, item.id)

  const getQty = (child: OrderingStudent, mealtime: Mealtime, item: DayRowItem): number => {
    if (mode === 'edit') {
      return editDraft[editKey(mealtime, child.userId, item.id)] ?? orderedQty(child, mealtime, item)
    }
    return (
      cartQtyByKey.get(cartLineKey(date, mealtime, child.accountMemberId, item.id, item.specialDietAccommodationId)) ??
      0
    )
  }

  const setQty = (child: OrderingStudent, mealtime: Mealtime, item: DayRowItem, qty: number): void => {
    // Special-diet items carry a per-student daily cap; clamp the client-side
    // (the server enforces it authoritatively too).
    const clamped = item.specialDietAccommodationId ? Math.min(1, qty) : qty
    if (mode === 'edit') {
      setEditDraft((d) => {
        const next = { ...d }
        if (clamped > 0) {
          for (const other of rowsByMealtime(child).get(mealtime) ?? []) {
            if (other.id !== item.id && (item.specialDietAccommodationId || other.specialDietAccommodationId)) {
              next[editKey(mealtime, child.userId, other.id)] = 0
            }
          }
        }
        next[editKey(mealtime, child.userId, item.id)] = clamped
        return next
      })
      return
    }
    const row = childLocationRow(child, location.id)
    const line: FamilyCartLine = {
      key: cartLineKey(date, mealtime, child.accountMemberId, item.id, item.specialDietAccommodationId),
      locationId: location.id,
      organizationId: location.organizationId,
      date,
      mealtime,
      studentMemberId: child.accountMemberId,
      studentUserId: child.userId,
      gradeRangeId: row?.gradeRangeId ?? null,
      menuItemId: item.id,
      name: menuItemName(item),
      imageUri: menuItemImageUri(item),
      unitPriceCents: item.customerPriceCents,
      currency: item.currency,
      quantity: clamped,
      ...(item.specialDietAccommodationId ? { specialDietAccommodationId: item.specialDietAccommodationId } : {}),
    }
    if (clamped > 0) {
      for (const other of cartLines) {
        if (conflictsWithSpecialDietSelection(line, other)) {
          setLine({ ...other, quantity: 0 })
        }
      }
    }
    setLine(line)
  }

  // EDIT-mode commit: per mealtime, replaceOrder the existing batch (or create
  // a new order for a mealtime added during the edit). Items collected across
  // all children for that mealtime, final qty = draft override ?? ordered.
  const desiredFor = (mealtime: Mealtime): Array<{ child: OrderingStudent; item: DayRowItem; qty: number }> => {
    // Each child's rows use their own band-gated list — the active child's
    // includes their approved special-diet items.
    const representable = location.children.flatMap((child) =>
      (rowsByMealtime(child).get(mealtime) ?? []).map((item) => ({ child, item })),
    )
    const rows = representable
      .map(({ child, item }) => ({ child, item, qty: getQty(child, mealtime, item) }))
      .filter((d) => d.qty > 0)
    // Preserve placed lines the children's row lists can't REPRESENT — a
    // special-diet line of a non-active child, or a line whose menu is no
    // longer offered to the child (band re-tag, menu drift). replaceOrder
    // replaces the whole batch, so omitting one silently drops it. A line
    // that HAS a row but was stepped to zero is a deliberate removal — it is
    // represented, so it is never re-added here.
    const seen = new Set(
      representable.map((r) => orderLineKey(r.child.userId, r.item.id, r.item.specialDietAccommodationId)),
    )
    const carried = unrepresentedPlacedLines(
      (batchesByMealtime.get(mealtime) ?? []).flatMap((batch) => batch.items),
      seen,
    )
    for (const line of carried) {
      const child = location.children.find((c) => c.userId === line.studentUserId)
      if (child) {
        rows.push({ child, item: placedLineToRow(line), qty: line.quantity })
      }
    }
    return rows
  }

  const computeChanges = (): OrderChange[] => {
    const cells: OrderChange[] = []
    for (const mealtime of mealtimes) {
      for (const child of location.children) {
        for (const item of rowsByMealtime(child).get(mealtime) ?? []) {
          cells.push({
            key: editKey(mealtime, child.userId, item.id),
            itemName: menuItemName(item),
            childName: child.displayName,
            mealtimeLabel: MEALTIME_LABELS[mealtime],
            oldQty: orderedQty(child, mealtime, item),
            newQty: getQty(child, mealtime, item),
          })
        }
      }
    }
    return diffOrderChanges(cells)
  }

  const commitEdits = async (): Promise<void> => {
    setSaving(true)
    // Distinguishes the two 402 shapes below: a REPLACE debit is atomic
    // (rolled back — nothing changed), while a NEW-mealtime submit saves
    // the batch payment-pending server-side.
    let phase: 'replace' | 'submit' = 'replace'
    try {
      for (const mealtime of mealtimes) {
        // Each child's lines go back to the batch that HOLDS that child;
        // children with no batch on this sitting are billed to their own
        // household as new orders. Posting a line into a sibling's batch
        // escapes replaceOrder's `excludeBatchId`, so the line loses its
        // meal-plan subsidy and re-prices at full family price (PROD-1295).
        const plan = planEditCommit({
          batches: batchesByMealtime.get(mealtime) ?? [],
          desired: desiredFor(mealtime).map((d) => ({
            studentUserId: d.child.userId,
            accountId: d.child.accountId,
            menuItemId: d.item.id,
            quantity: d.qty,
            specialDietAccommodationId: d.item.specialDietAccommodationId ?? null,
          })),
        })
        for (const replace of plan.replaces) {
          phase = 'replace'
          await replaceMutation.mutateAsync({
            orderBatchId: replace.orderBatchId,
            items: replace.items.map(toOrderLinePayload),
          })
        }
        for (const create of plan.creates) {
          phase = 'submit'
          await submitFamilyOrderGroup({
            accountId: create.accountId,
            organizationId: location.organizationId,
            serviceLocationId: location.id,
            serviceDate: isoDate,
            mealtime,
            items: create.items.map(toOrderLinePayload),
          })
        }
      }
      setEditDraft({})
      setPendingChanges(null)
      setEditing(false)
      await ordersQuery.refetch()
      // The edit may have consumed / freed the per-sitting subsidy
      // allowance — recompute personalized previews. (The replace path
      // already invalidates via its mutation; this covers the direct
      // submitFamilyOrderGroup call for mealtimes added during the edit.)
      void queryClient.invalidateQueries({ queryKey: ['family-order-price-preview'] })
      toast.success('Order updated', { description: 'Your changes were saved.' })
    } catch (error) {
      if (isPaymentError(error)) {
        // Friendly shortfall + the add-funds returnTo path instead of the
        // raw ledger message (launch-gap 09).
        const shortfall = parsePaymentShortfall(error.body)
        const line = paymentShortfallLine(shortfall)
        // SPONSOR = the school's meal-plan wallet — no add-funds CTA; the
        // parent can't top up the school's account.
        const sponsorShortfall = shortfall.payer === 'SPONSOR'
        const addFundsAction = sponsorShortfall
          ? undefined
          : {
              label: 'Add funds',
              onClick: () => void checkoutNavigate(withReturnTo('/billing/family', returnHerePath)),
            }
        if (phase === 'submit') {
          // The ADDED mealtime was saved payment-pending server-side —
          // exit the edit and refetch so the pending banner takes over.
          toast.error('Added items saved — payment didn’t go through', {
            description: sponsorShortfall
              ? (line ?? 'The school’s meal-plan balance couldn’t cover its share.')
              : `${line ?? 'Your balance doesn’t cover the added items.'} Add funds, then complete the order from this day.`,
            action: addFundsAction,
          })
          setEditDraft({})
          setPendingChanges(null)
          setEditing(false)
          void ordersQuery.refetch()
        } else {
          // A replace debit is atomic — nothing changed; keep the draft
          // so the parent can add funds and save again.
          toast.error(sponsorShortfall ? 'School meal-plan balance is out' : 'Not enough funds for this change', {
            description: sponsorShortfall
              ? `${line ?? 'The school’s meal-plan balance couldn’t cover its share.'} Nothing was changed.`
              : `${line ?? 'Your balance doesn’t cover the increase.'} Nothing was changed — add funds, then save again.`,
            action: addFundsAction,
          })
          setPendingChanges(null)
        }
      } else {
        toast.error('Could not update order', { description: error instanceof Error ? error.message : String(error) })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSave = (): void => {
    const changes = computeChanges()
    if (changes.length > 0) {
      setPendingChanges(changes)
      return
    }
    void commitEdits()
  }

  // EDIT-mode footer: signed NET change vs the placed order (see
  // `editDeltaCents` for the pricing rules — browse price for additions,
  // paid-net price for removals).
  const editDelta = useMemo(() => {
    if (mode !== 'edit') {
      return { deltaCents: 0, changedCells: 0 }
    }
    // Per-cell paid line totals from the placed batches, keyed like editKey.
    const paidCentsByKey = new Map<string, number>()
    for (const [mealtime, orders] of batchesByMealtime) {
      for (const order of orders) {
        for (const orderItem of order.items) {
          const key = editKey(mealtime, orderItem.studentUserId ?? '', orderItem.menuItemId)
          paidCentsByKey.set(key, (paidCentsByKey.get(key) ?? 0) + orderItem.pricing.totalCents)
        }
      }
    }
    const cells: OrderDeltaCell[] = []
    for (const mealtime of mealtimes) {
      for (const child of children) {
        for (const item of rowsByMealtime(child).get(mealtime) ?? []) {
          const key = editKey(mealtime, child.userId, item.id)
          const oldQty = orderedQty(child, mealtime, item)
          cells.push({
            oldQty,
            newQty: editDraft[key] ?? oldQty,
            browseUnitPriceCents: item.customerPriceCents ?? 0,
            paidLineCents: paidCentsByKey.get(key) ?? null,
          })
        }
      }
    }
    return editDeltaCents(cells)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orderedQty reads alreadyOrdered
  }, [
    mode,
    mealtimes,
    children,
    orderableItemsByChild,
    activeChildItemsByMealtime,
    editDraft,
    batchesByMealtime,
    alreadyOrdered,
  ])
  const editCurrency = placedBatches[0]?.currency ?? 'USD'
  const placedQty = useMemo(() => {
    let n = 0
    for (const order of placedBatches) {
      for (const item of order.items) {
        // Cancelled/refunded lines are shown muted on the receipt but no
        // longer count as placed items.
        if ((item.lineState ?? 'active') === 'active') {
          n += item.quantity
        }
      }
    }
    return n
  }, [placedBatches])
  // Per-child item count for the tab badge. ADD counts the cart (gray badge);
  // RECEIPT/EDIT count the placed (or live-edited) order (green badge).
  const childItemCount = (child: OrderingStudent): number => {
    if (mode === 'add') {
      return cartLines
        .filter((l) => l.date === date && l.studentMemberId === child.accountMemberId)
        .reduce((n, l) => n + l.quantity, 0)
    }
    if (mode === 'edit' && childLocationRow(child, location.id)) {
      let n = 0
      for (const [mealtime, items] of rowsByMealtime(child)) {
        for (const item of items) {
          n += getQty(child, mealtime, item)
        }
      }
      return n
    }
    return alreadyOrdered.itemsForChildOnDay(date, child.userId).reduce((n, it) => n + it.quantity, 0)
  }
  const childBadgeTone: 'ordered' | 'cart' = mode === 'add' ? 'cart' : 'ordered'

  const cartCount = totalQty(cartLines)
  const cartGross = subtotalCents(cartLines)
  const cartCurrency = cartLines.find((l) => l.currency)?.currency ?? 'USD'

  return (
    <Sheet>
      <div className="flex h-full min-h-0 flex-col">
        <DayHeader dateKey={date} onClose={requestClose} />
        {/* The payment-pending banner supersedes the status row — stacking
            "Payment pending" over the banner saying the same thing is noise. */}
        {/* No countdown on a day the school isn't serving — "Due in 3 days" over
            a "No school meals this day" empty state is the exact contradiction
            Ops reported. The empty state below carries the explanation. */}
        {hasPendingPayment || noServiceOverride ? null : <DayStatusRow badge={badge} timeZone={location.timeZone} />}

        {hasPendingPayment ? (
          <div className="shrink-0 border-b bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            Your payment didn’t go through — your order is saved and nothing was charged.{' '}
            <button
              type="button"
              onClick={() => void checkoutNavigate(withReturnTo('/billing/family', returnHerePath))}
              className="font-semibold underline underline-offset-2">
              Add funds
            </button>
            , then{' '}
            <button
              type="button"
              onClick={() => void retryPaymentNow()}
              disabled={retryPaymentMutation.isPending || cancelRetryMutation.isPending}
              className="font-semibold underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
              {retryPaymentMutation.isPending ? 'completing…' : 'complete it now'}
            </button>
            .{automaticRetryClause ? ` ${automaticRetryClause}` : ''}
          </div>
        ) : null}

        {isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : !activeChild ? (
          <EmptyState
            icon={<CalendarOff className="h-10 w-10" />}
            title="No students"
            description="Add a child to your family to order for them."
            padding="sm"
          />
        ) : (
          <Tabs
            value={activeChild.accountMemberId}
            onValueChange={setActiveChildId}
            className="flex min-h-0 flex-1 flex-col">
            <TabsList className={cn(TABS_LIST_CLASS, 'shrink-0 overflow-x-auto px-4')}>
              {children.map((child) => {
                const count = childItemCount(child)
                return (
                  <TabsTrigger key={child.accountMemberId} value={child.accountMemberId} className={TABS_TRIGGER_CLASS}>
                    <UserAvatar
                      name={child.displayName}
                      initials={child.initials}
                      avatarUri={child.avatarUri}
                      avatarColor={child.avatarColor}
                      size="sm"
                    />
                    {child.displayName}
                    <span
                      className={cn(
                        'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums',
                        count > 0 && childBadgeTone === 'ordered'
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                          : 'bg-muted text-muted-foreground',
                      )}>
                      {count}
                    </span>
                  </TabsTrigger>
                )
              })}
            </TabsList>
            {children.map((child) => (
              <TabsContent
                key={child.accountMemberId}
                value={child.accountMemberId}
                className="mt-0 min-h-0 flex-1 overflow-y-auto">
                {/* Per-child emptiness: a band-blocked child (or a band-less
                    student, fail-closed) has zero orderable mealtimes and
                    lands on the empty state even when siblings have food. */}
                {mealtimesFor(child).length === 0 || noServiceOverride ? (
                  // Center in the dialog's full height — top-aligned, the
                  // card sat over a sea of dead whitespace.
                  <div className="flex h-full min-h-64 items-center justify-center p-6">
                    <FamilyDayEmptyState
                      reason={noMenuReason}
                      schoolName={location.name}
                      date={date}
                      onGoToDate={(dayKey) =>
                        void checkoutNavigate(`/orders/family/day?date=${encodeURIComponent(dayKey)}`)
                      }
                      onGoToThisWeek={onClose}
                    />
                  </div>
                ) : (
                  <ChildMealtimeList
                    child={child}
                    serviceDate={isoDate}
                    mealtimes={mealtimesFor(child)}
                    itemsByMealtime={rowsByMealtime(child)}
                    autoOrderSkipped={autoOrderSkippedFor(child)}
                    getQty={(mealtime, item) => getQty(child, mealtime, item)}
                    onSetQty={(mealtime, item, qty) => setQty(child, mealtime, item, qty)}
                    orderedQty={(mealtime, item) => orderedQty(child, mealtime, item)}
                    inactiveFor={(mealtime, item) => inactiveLineFor(mealtime, child.userId, item.id)}
                    previewFor={(mealtime, item) => previewFor(child, mealtime, item)}
                    substitutedNameFor={(mealtime, item) => {
                      const line = (batchesByMealtime.get(mealtime) ?? [])
                        .flatMap((batch) => batch.items)
                        .find((l) => l.studentUserId === child.userId && l.menuItemId === item.id)
                      return line?.substituted ? line.title : null
                    }}
                    orderedByFor={(mealtime, item) =>
                      (batchesByMealtime.get(mealtime) ?? []).find((batch) =>
                        batch.items.some((line) => line.studentUserId === child.userId && line.menuItemId === item.id),
                      )?.orderedBy
                    }
                    sharedOrderFor={(mealtime) =>
                      (batchesByMealtime.get(mealtime) ?? []).some(
                        (batch) =>
                          batch.sharedReadOnly && batch.items.some((line) => line.studentUserId === child.userId),
                      )
                    }
                    readOnly={readOnly}
                    viewOnly={viewOnly}
                    receipt={mode === 'receipt'}
                    holidayTreats={data?.holidayTreats ?? []}
                  />
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Footer follows the ACTIVE child's tab — hidden when their tab
            shows the empty state (e.g. band-blocked child, no orderable
            menus), matching the panel above. */}
        {!activeChild || mealtimesFor(activeChild).length === 0 || noServiceOverride ? null : mode === 'edit' ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-3">
            <div className="flex items-center gap-3">
              <ShoppingCart className="h-6 w-6 shrink-0 text-foreground" strokeWidth={1.75} />
              <div className="flex flex-col leading-tight">
                <span
                  className={cn(
                    'text-base font-semibold',
                    editDelta.deltaCents > 0
                      ? 'text-foreground'
                      : editDelta.deltaCents < 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-muted-foreground',
                  )}>
                  {editDelta.deltaCents > 0 ? '+' : editDelta.deltaCents < 0 ? '−' : ''}
                  {formatCents(Math.abs(editDelta.deltaCents), editCurrency)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {editDelta.changedCells === 0
                    ? 'No changes yet'
                    : `${editDelta.changedCells} ${editDelta.changedCells === 1 ? 'change' : 'changes'}`}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        ) : mode === 'receipt' ? (
          hasPendingPayment ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-3">
              <span className="text-sm text-muted-foreground">
                Payment pending · {placedQty} {placedQty === 1 ? 'item' : 'items'}
              </span>
              <button
                type="button"
                onClick={() => void cancelPaymentRetry()}
                disabled={cancelRetryMutation.isPending || retryPaymentMutation.isPending}
                className="inline-flex items-center justify-center rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">
                {cancelRetryMutation.isPending
                  ? 'Cancelling…'
                  : hasScheduledRetry
                    ? 'Cancel automatic retry'
                    : 'Cancel order'}
              </button>
            </div>
          ) : canEdit ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-3">
              <span className="text-sm text-muted-foreground">
                Order placed · {placedQty} {placedQty === 1 ? 'item' : 'items'}
              </span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
                Edit order
              </button>
            </div>
          ) : null
        ) : !readOnly ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-3">
            {/* Discreet "See cart" — the cart summary itself links to checkout. */}
            <button
              type="button"
              onClick={goToCheckout}
              disabled={cartCount === 0}
              aria-label="See cart"
              className="-mx-2 -my-1 flex items-center gap-3 rounded-lg px-2 py-1 text-left transition-colors enabled:hover:bg-muted/50">
              <ShoppingCart className="h-6 w-6 shrink-0 text-foreground" strokeWidth={1.75} />
              <div className="flex flex-col leading-tight">
                <CartTotalPrice
                  totals={cartPreview.totals}
                  fallbackCents={cartGross}
                  currency={cartCurrency}
                  className="text-base font-semibold text-foreground"
                />
                <span className="text-xs text-muted-foreground">
                  {cartCount} {cartCount === 1 ? 'item' : 'items'}
                </span>
              </div>
              {cartCount > 0 ? <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" /> : null}
            </button>
            {weekCta.kind === 'checkout' ? (
              <button
                type="button"
                onClick={goToCheckout}
                disabled={cartCount === 0}
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
                Review &amp; Checkout
              </button>
            ) : (
              <button
                type="button"
                onClick={() => goToDay(weekCta.targetDate)}
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
                {weekCta.kind === 'next-day' ? 'Next day' : `Back to ${weekdayLong(weekCta.targetDate)}`}
              </button>
            )}
          </div>
        ) : null}
      </div>

      <AlertDialog open={pendingChanges !== null} onOpenChange={(open) => !open && setPendingChanges(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit {dayLabel} order?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;re about to update an order that was already placed. Review updates below and confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="divide-y text-sm">
            {(pendingChanges ?? []).map((change) => (
              <li key={change.key} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-foreground">{change.itemName}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {change.childName} · {change.mealtimeLabel}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-3 tabular-nums">
                  <span className="relative text-muted-foreground">
                    {change.oldQty}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-x-[-5px] top-1/2 h-px -translate-y-1/2 bg-current"
                    />
                  </span>
                  <span className="font-semibold text-foreground">{change.newQty}</span>
                </span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void commitEdits()}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={discardIntent !== null} onOpenChange={(open) => !open && setDiscardIntent(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard order changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this order. {discardIntent === 'close' ? 'Closing' : 'Leaving edit mode'} will
              discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  )
}

/**
 * Item subtitle: the price, then (after an interpunct) "Contains …" when the
 * item carries reviewed allergen tags, then the item's dietary tags as
 * badges. An allergen-free item shows NO allergen text — "Allergen-free" is
 * deliberately not claimed on the ordering surface; allergen detail lives on
 * the nutrition surfaces. Tags render only when the menu query opted in.
 *
 * When a personalized preview says the student's subsidy covers (part of)
 * this item, the gross price renders struck through next to the net price
 * plus a subtle Covered / Reduced badge. An allowance-exhausted item shows
 * the plain full price (no badge) — server-computed, never local math.
 */
function ItemSubtitle({
  item,
  preview,
  hidePrice = false,
}: {
  item: ActiveMenuItem
  preview?: FamilyOrderPricePreviewLineWire | null
  /** View-only campus (school ordering only): the school pays, so no price,
   *  no "Price not set" and no subsidy badge — allergens and chips stay. */
  hidePrice?: boolean
}): ReactElement {
  // ZERO-source guard: an UNPRICED item (no tier matched) reads
  // "Price not set" and cannot be added — parents must never order a
  // line the school hasn't priced. A deliberate $0 item stays orderable.
  const unpriced = !hidePrice && item.isPriced === false
  const badge = !hidePrice && !unpriced && preview ? previewBadge(preview) : null
  const price =
    !hidePrice && !unpriced && item.customerPriceCents != null && item.customerPriceCents > 0
      ? formatCents(item.customerPriceCents, item.currency ?? 'USD')
      : null
  const allergens = item.allergens
  const dietaryTags = item.dietaryTags ?? []
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
      {unpriced ? <span className="italic">Price not set</span> : null}
      {badge && preview ? (
        <span className="inline-flex items-center gap-1.5">
          <s className="text-muted-foreground/70">{formatCents(preview.grossUnitCents, preview.currency)}</s>
          <span className="font-medium text-foreground">{formatCents(preview.netCents, preview.currency)}</span>
          <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium leading-none text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            {badge === 'covered' ? 'Covered' : 'Reduced'}
          </span>
        </span>
      ) : price ? (
        <span>{price}</span>
      ) : null}
      {(price || unpriced) && allergens != null && allergens.length > 0 ? <span aria-hidden>·</span> : null}
      {allergens != null && allergens.length > 0 ? (
        <span>Contains {allergens.map((a) => a.name.toLowerCase()).join(', ')}</span>
      ) : null}
      <DietaryTagChips tags={dietaryTags} />
    </span>
  )
}

export function ChildMealtimeList({
  child,
  serviceDate,
  mealtimes,
  itemsByMealtime,
  autoOrderSkipped,
  getQty,
  onSetQty,
  orderedQty,
  inactiveFor,
  previewFor,
  substitutedNameFor,
  orderedByFor,
  sharedOrderFor,
  readOnly,
  viewOnly = false,
  receipt = false,
  holidayTreats = [],
}: {
  child: OrderingStudent
  /** The day on screen (`YYYY-MM-DD`) — the item detail modal describes items AS SERVED that day (PROD-1386). */
  serviceDate: string
  mealtimes: Mealtime[]
  itemsByMealtime: ReadonlyMap<Mealtime, DayRowItem[]>
  /** Free holiday treats served today — one gift row per mealtime that has one. */
  holidayTreats?: readonly HolidayTreatWire[]
  /** Mealtimes whose auto-order stood down because the slot was hand-ordered
   *  (PROD-1037) — captioned under the mealtime header. */
  autoOrderSkipped?: ReadonlySet<Mealtime>
  getQty: (mealtime: Mealtime, item: DayRowItem) => number
  onSetQty: (mealtime: Mealtime, item: DayRowItem, qty: number) => void
  orderedQty: (mealtime: Mealtime, item: DayRowItem) => number
  /** Cancelled/refunded units for this child's row — muted receipt rows. */
  inactiveFor?: (mealtime: Mealtime, item: DayRowItem) => InactiveOrderLine | null
  /** Personalized price preview for this child's row, when loaded. */
  previewFor?: (mealtime: Mealtime, item: DayRowItem) => FamilyOrderPricePreviewLineWire | null
  /** Chef pre-approved swap: the placed line's re-rendered title, when the
   *  kitchen replaced this row's component — must win over the live-menu
   *  name (the menu still carries the original). */
  substitutedNameFor?: (mealtime: Mealtime, item: DayRowItem) => string | null
  orderedByFor?: (mealtime: Mealtime, item: DayRowItem) => 'PARENT' | 'SCHOOL_ADMIN' | undefined
  sharedOrderFor?: (mealtime: Mealtime) => boolean
  readOnly: boolean
  /**
   * The child's campus takes no parent orders (school ordering only,
   * PROD-1554): rows show the menu (tap for nutrition and ingredients) with
   * no steppers and no prices. Placed rows keep their "N ordered" pill.
   */
  viewOnly?: boolean
  /** RECEIPT view: only ordered items, quantities shown as a static count. */
  receipt?: boolean
}): ReactElement {
  const [detailItemId, setDetailItemId] = useState<string | null>(null)
  // In receipt mode show only the items this child actually ordered, plus
  // cancelled/refunded lines rendered muted with a badge — an inactive line
  // must stay visible as inactive, never pass for a live meal or vanish.
  const visibleItems = (mealtime: Mealtime): DayRowItem[] => {
    const items = itemsByMealtime.get(mealtime) ?? []
    return receipt
      ? items.filter((item) => orderedQty(mealtime, item) > 0 || inactiveFor?.(mealtime, item) != null)
      : items
  }
  // Distinct owning-menu sections within a mealtime — headers render only
  // when the mealtime actually spans more than one menu.
  const menuSectionTitlesFor = (items: readonly DayRowItem[]): Set<string> => {
    const titles = new Set<string>()
    for (const item of items) {
      if (item.menuGroupTitle !== undefined) {
        titles.add(item.menuGroupTitle)
      }
    }
    return titles
  }
  return (
    <div className="flex flex-col pb-6">
      {mealtimes.map((mealtime) => {
        const items = visibleItems(mealtime)
        const menuSectionTitles = menuSectionTitlesFor(items)
        // RECEIPT keeps every mealtime (with a placeholder when nothing was
        // ordered); ADD/EDIT only skip mealtimes that have no menu at all.
        if (items.length === 0 && !receipt) {
          return null
        }
        return (
          <div key={mealtime}>
            {/* Mealtime breaker row */}
            <div className="bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {MEALTIME_LABELS[mealtime]}
            </div>
            {autoOrderSkipped?.has(mealtime) ? (
              <p className="px-4 pt-2 text-xs text-muted-foreground">
                Auto-order skipped. An order was already placed for {MEALTIME_LABELS[mealtime].toLowerCase()}.
              </p>
            ) : null}
            {items.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">
                No meals ordered for {MEALTIME_LABELS[mealtime].toLowerCase()}
              </p>
            ) : (
              <ul className="divide-y">
                {items.map((item, index) => {
                  // Menus render as sections within the mealtime ("Mains",
                  // "Sides, Drinks & Desserts") — a header opens each run of
                  // rows from a new menu, only when the mealtime actually
                  // spans more than one menu. Rows appended outside the
                  // flatten (special-diet additions) carry no group and ride
                  // the section they follow.
                  const sectionTitle =
                    (menuSectionTitles.size > 1 || item.specialDietAccommodationId) &&
                    item.menuGroupTitle !== undefined &&
                    item.menuGroupTitle !== items[index - 1]?.menuGroupTitle
                      ? item.menuGroupTitle
                      : null
                  const ordered = orderedQty(mealtime, item)
                  const orderedBy = ordered > 0 ? orderedByFor?.(mealtime, item) : undefined
                  const inactive = receipt ? (inactiveFor?.(mealtime, item) ?? null) : null
                  // Muted only when NO live units remain (a partial refund
                  // keeps the row live and just adds the badge).
                  const fullyInactive = inactive !== null && ordered === 0
                  const imageUri = menuItemImageUri(item)
                  return (
                    <Fragment key={`${child.accountMemberId}:${mealtime}:${item.id}`}>
                      {sectionTitle !== null ? (
                        <li className="px-4 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
                          {sectionTitle}
                        </li>
                      ) : null}
                      <li className={cn('flex items-center gap-3 px-4 py-3', fullyInactive && 'opacity-60')}>
                        <button
                          type="button"
                          onClick={() => setDetailItemId(item.id)}
                          title="View item details"
                          className="group flex min-w-0 flex-1 items-center gap-3 text-left focus:outline-none focus-visible:outline-none">
                          <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted">
                            <AssetImage
                              src={imageUri}
                              className="h-full w-full object-cover"
                              fallback={<UtensilsCrossed className="h-5 w-5 text-muted-foreground" />}
                            />
                          </span>
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <span className="truncate text-base font-medium text-foreground group-hover:underline">
                              {((receipt ? substitutedNameFor?.(mealtime, item) : null) ?? menuItemName(item)).replace(
                                item.serviceBadge?.kind === 'field-trip' ? /^Field Trip Meal: / : /$^/,
                                '',
                              )}
                            </span>
                            {item.serviceBadge && <MenuServiceBadge {...item.serviceBadge} />}
                            {item.specialDietAccommodationId ? (
                              <span className="flex">
                                <Badge variant="secondary" className="text-[10px] font-medium">
                                  Approved for {child.displayName}
                                </Badge>
                              </span>
                            ) : null}
                            {inactive ? (
                              <span className="flex">
                                <Badge variant="secondary" className="text-[10px] font-medium text-muted-foreground">
                                  {inactive.state === 'refunded' ? 'Refunded' : 'Cancelled'}
                                  {ordered > 0 ? ` ×${inactive.quantity}` : ''}
                                </Badge>
                              </span>
                            ) : null}
                            {orderedBy && (
                              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                {orderedBy === 'SCHOOL_ADMIN' ? 'Ordered by School Admin' : 'Ordered by Parent'}
                              </span>
                            )}
                            {orderedBy === 'SCHOOL_ADMIN' ? (
                              <span className="text-xs text-muted-foreground">Paid by school</span>
                            ) : (
                              <ItemSubtitle
                                item={item}
                                preview={previewFor?.(mealtime, item) ?? null}
                                hidePrice={viewOnly}
                              />
                            )}
                          </span>
                        </button>
                        <div className="flex shrink-0 items-center gap-3">
                          {receipt ? (
                            fullyInactive ? (
                              <span className="text-base font-semibold tabular-nums text-muted-foreground line-through">
                                ×{inactive.quantity}
                              </span>
                            ) : (
                              <span className="text-base font-semibold tabular-nums text-foreground">×{ordered}</span>
                            )
                          ) : (
                            <>
                              {ordered > 0 ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                  {ordered} ordered
                                </span>
                              ) : null}
                              {viewOnly ? null : (
                                <QtyStepper
                                  max={item.specialDietAccommodationId ? 1 : undefined}
                                  value={getQty(mealtime, item)}
                                  onChange={(next) => onSetQty(mealtime, item, next)}
                                  // Unpriced items can't be added by parents —
                                  // see the ZERO-source guard in ItemSubtitle.
                                  disabled={readOnly || Boolean(sharedOrderFor?.(mealtime)) || item.isPriced === false}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </li>
                    </Fragment>
                  )
                })}
              </ul>
            )}
            {(() => {
              // Sephora moment: the free treat rides every meal at this
              // mealtime on its date — teaser while the child has none, "Gift
              // added ×N" once they do. Never a stepper, never a price.
              const treat = holidayTreats.find((t) => t.mealtime === mealtime)
              if (!treat || items.length === 0) {
                return null
              }
              const mealCount = items.reduce(
                (sum, item) =>
                  sum +
                  (receipt ? orderedQty(mealtime, item) : Math.max(getQty(mealtime, item), orderedQty(mealtime, item))),
                0,
              )
              return (
                <ul className="border-t">
                  <HolidayTreatGiftRow
                    treat={treat}
                    mealCount={mealCount}
                    mealtimeLabel={MEALTIME_LABELS[mealtime]}
                    // A view-only campus never sells the meal the treat rides
                    // on, so it reads as a receipt, never an "add a lunch" teaser.
                    receipt={receipt || viewOnly}
                    onOpenDetail={setDetailItemId}
                  />
                </ul>
              )
            })()}
          </div>
        )
      })}
      <MenuItemDetailModal
        menuItemId={detailItemId}
        serviceDate={serviceDate}
        open={detailItemId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDetailItemId(null)
          }
        }}
      />
    </div>
  )
}
